import { VariantColorsList } from '../../common.js';

/// /////////////////////////////////////////////////
const solidStyle = variantColor => {
  return {
    backgroundColor: `${variantColor}500`,
    color: 'background',
    _hover: {
      backgroundColor: `${variantColor}400`
    },
    _focus: {
      backgroundColor: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _focusVisible: {
      backgroundColor: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _active: {
      transition: 'none',
      backgroundColor: `${variantColor}600`
    },
    _disabled: {
      backgroundColor: 'carbon200',
      cursor: 'not-allowed'
    }
  };
};
const outlineStyle = variantColor => {
  return {
    _before: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 'radiusDefault',
      borderWidth: '1px',
      borderStyle: 'solid'
    },
    borderColor: `${variantColor}500`,
    color: `${variantColor}500`,
    _hover: {
      borderColor: `${variantColor}400`,
      color: `${variantColor}400`
    },
    _focus: {
      color: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _focusVisible: {
      color: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _active: {
      transition: 'none',
      borderColor: `${variantColor}600`,
      color: `${variantColor}600`
    },
    _disabled: {
      color: 'carbon200',
      cursor: 'not-allowed'
    }
  };
};
const linkStyle = variantColor => {
  return {
    color: `${variantColor}500`,
    _hover: {
      textDecoration: 'underline',
      color: `${variantColor}400`
    },
    _focus: {
      color: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _focusVisible: {
      color: `${variantColor}400`,
      boxShadow: 'focus'
    },
    _active: {
      transition: 'none',
      color: `${variantColor}600`
    },
    _disabled: {
      color: 'carbon200',
      cursor: 'not-allowed'
    }
  };
};
const tertiaryStyle = {
  color: `brandPrimary500`,
  _hover: {
    textDecoration: 'underline',
    color: `brandPrimary400`
  },
  _active: {
    transition: 'none',
    color: `brandPrimary600`
  },
  _focus: {
    textDecoration: 'underline'
  },
  _focusVisible: {
    textDecoration: 'underline',
    boxShadow: 'focus'
  },
  _disabled: {
    color: 'carbon200',
    cursor: 'not-allowed'
  }
};
const unstyledStyle = variantColor => {
  return {
    color: `${variantColor}500`,
    _hover: {
      backgroundColor: `${variantColor}100`
    },
    _active: {
      transition: 'none',
      backgroundColor: `${variantColor}200`
    },
    _disabled: {
      color: 'carbon200',
      cursor: 'not-allowed'
    }
  };
};
const noneStyle = () => {
  return {
    _disabled: {
      color: 'carbon200',
      cursor: 'not-allowed'
    }
  };
};
const variantProps = props => {
  const {
    variant,
    variantColor
  } = props;
  switch (variant) {
    case 'primary':
      return solidStyle(VariantColorsList.brandPrimary);
    case 'secondary':
      return outlineStyle(VariantColorsList.brandPrimary);
    case 'tertiary':
      return tertiaryStyle;
    case 'tertiary-light':
      return {
        ...tertiaryStyle,
        fontWeight: 'normal'
      };
    case 'danger':
      return outlineStyle(VariantColorsList.watermelon);
    case 'outline':
      return outlineStyle(variantColor);
    case 'link':
      return linkStyle(variantColor);
    case 'unstyled':
      return unstyledStyle(variantColor);
    case 'none':
      return noneStyle();
    case 'solid':
    default:
      return solidStyle(variantColor);
  }
};
/// /////////////////////////////////////////////////
const baseStyles = {
  alignItems: 'center',
  appearance: 'none',
  border: 'none',
  borderRadius: 'radiusDefault',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'normal',
  fontWeight: 'semi',
  fontSize: 'fontSize20',
  lineHeight: 'lineHeight20',
  justifyContent: 'center',
  outline: 'none',
  position: 'relative',
  transition: 'all .15s ease-in-out',
  transitionProperty: 'background-color, box-shadow',
  userSelect: 'none',
  _focusVisible: {
    boxShadow: 'focus'
  }
};
/// /////////////////////////////////////////////////
const getButtonStyles = props => {
  return {
    ...baseStyles,
    ...variantProps(props)
  };
};
/* ======================================= */
const getButtonSizes = size => {
  switch (size) {
    case 'small':
      return {
        fontSize: 'fontSize10',
        lineHeight: 'lineHeight10',
        height: '24px',
        py: 'space10',
        px: 'space30'
      };
    case 'medium':
      return {
        height: '32px',
        py: 'space20',
        px: 'space50'
      };
    case 'large':
      return {
        height: '40px',
        py: 'space30',
        px: 'space60'
      };
    case 'none':
    default:
      return {
        p: 'space0'
      };
  }
};
const tertiarySizesMap = {
  small: {
    fontSize: 'fontSize10',
    lineHeight: 'lineHeight10'
  },
  large: {
    fontSize: 'fontSize20',
    lineHeight: 'lineHeight20'
  }
};
const getTertiarySizes = size => {
  return tertiarySizesMap[size] || tertiarySizesMap.large;
};
const getIconSize = (isTertiaryVariant, size) => {
  if (isTertiaryVariant) {
    switch (size) {
      case 'small':
        return 'small';
      case 'large':
      default:
        return 'large';
    }
  }
  return size;
};
const checkForTertiaryVariant = variant => ['tertiary', 'tertiary-light'].includes(variant);

export { checkForTertiaryVariant, getButtonSizes, getButtonStyles, getIconSize, getTertiarySizes, linkStyle, noneStyle, outlineStyle, solidStyle, tertiarySizesMap, tertiaryStyle, unstyledStyle, variantProps };
